import React from 'react';

import { Botao, Chip, Modal } from '../../../ui';
import { PainelVistoriaItem } from '../../../api/vistoria/vistoriaApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { totalFotosEsperado } from '../../../utils/vistoriaLegendas';
import {
  pendenciasDaVistoria,
  rotuloEnvioSga,
  rotuloEstadoVistoria,
  ROTULO_ASSOCIADO_PENDENTE_SGA,
} from '../../../utils/vistoriaFluxo';

export interface ModalDetalhesVistoriaProps {
  /** Item em exibição — null mantém o modal fechado. */
  item: PainelVistoriaItem | null;
  /** Preload de "Ver laudo" em andamento (at-24) — vira estado no botão. */
  abrindoLaudo?: boolean;
  onFechar: () => void;
  onIniciarVistoria: (item: PainelVistoriaItem) => void;
  onContinuarVistoria: (item: PainelVistoriaItem) => void;
  onVerLaudo: (item: PainelVistoriaItem) => void;
  onDescartar: (item: PainelVistoriaItem) => void;
}

/**
 * Detalhes de um veículo do painel de vistorias (at-32; reescrito na at-63).
 *
 * Decisão de Victor (01/09/2026, at-32) que ele atende: **só o ESTADO vai na linha
 * da tabela**; fotos, envio ao SGA e todas as ações vivem aqui. A tabela é índice.
 *
 * ## O que mudou na at-63 (frente D/D2)
 *
 *   1. **Virou o `Modal` do kit.** O overlay era manual (`.filtros-overlay` +
 *      `.carteira-detalhes-modal`), com listener de `Escape` próprio e
 *      `aria-modal="true"` **sem prender o foco** — a promessa falsa ao leitor de
 *      tela que o `Modal` existe para encerrar. Ganhou de graça: foco que entra e
 *      volta, Tab circulando dentro, e o título como `<h2>` ligado por
 *      `aria-labelledby`.
 *   2. 🔴 **Os marcadores VERMELHOS viraram âmbar.** "Associado pendente no SGA" e
 *      "aguardando envio ao SGA" usavam `vistoria-badge-alerta`/`-chip-alerta`, e
 *      nada disso é erro — é *"ainda não"* (spec 02 §2.3). A regra de texto+cor mora
 *      em `utils/vistoriaFluxo.ts`, não aqui.
 *   3. **O `×` do fechar era um caractere literal** num `<button>`; agora é o
 *      `Icone` do `Modal`.
 *   4. **"Ver laudo" mostra que está carregando.** O preload
 *      (`getUltimaVistoriaPorVeiculo` antes de navegar, para o laudo não abrir
 *      vazio) rodava sob um `FullScreenLoader` de tela cheia; virou estado do botão.
 *   5. **As pendências passaram a ser calculadas AQUI**, pelo util, em vez de
 *      chegarem por uma prop `pendencias` que o painel montava. Era a mesma regra
 *      viva em dois lugares (a convenção da at-24 proíbe).
 *
 * ⚠️ **Este componente dependia de `vistoria.css` SEM importá-lo** — funcionava só
 * porque o painel o importava. Aquele arquivo foi apagado na at-63, e é por isso
 * que os dois tinham de migrar na mesma entrega.
 */
const ModalDetalhesVistoria: React.FC<ModalDetalhesVistoriaProps> = ({
  item,
  abrindoLaudo = false,
  onFechar,
  onIniciarVistoria,
  onContinuarVistoria,
  onVerLaudo,
  onDescartar,
}) => {
  if (!item) return null;

  const { vistoria, associado } = item;
  const emAndamento = !!vistoria && !vistoria.concluida;
  const estado = rotuloEstadoVistoria(item);

  const pendencias = vistoria
    ? pendenciasDaVistoria(vistoria.proximoPasso, vistoria.quantidadeFotos, item.tipoVeiculo)
    : [];

  return (
    <Modal
      aberto
      titulo={associado.nomeCompleto || 'Veículo sem associado'}
      descricao={formatCpfCnpj(String(associado.cpfCnpj || ''))}
      aoFechar={onFechar}
    >
      <div className="tw-flex tw-flex-col tw-gap-5">
        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            Veículo
          </h3>

          <p className="tw-m-0 tw-font-marca tw-text-base tw-font-bold tw-text-texto-titulo">
            {item.placa || '—'}
          </p>
          <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-texto-suave">
            {/* at-46: a "placa" da motocicleta elétrica é a identificação gerada
                pelo SAAC — dizer o tipo evita o consultor achar que é placa real. */}
            {item.bikeEletrica ? 'Motocicleta elétrica · ' : ''}
            {item.modelo || '—'}
            {item.anoModelo ? ` · ${item.anoModelo}` : ''}
          </p>

          {!associado.podeVistoriar && (
            <div className="tw-mt-2">
              <Chip
                variante={ROTULO_ASSOCIADO_PENDENTE_SGA.variante}
                title="O associado precisa estar cadastrado no SGA antes da vistoria"
              >
                {ROTULO_ASSOCIADO_PENDENTE_SGA.texto}
              </Chip>
            </div>
          )}
        </section>

        <section>
          <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
            Vistoria
          </h3>

          <Chip variante={estado.variante}>{estado.texto}</Chip>

          {vistoria?.concluida && (
            <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
              {vistoria.quantidadeFotos} foto(s) · assinada
            </p>
          )}

          {/* Vistoria existe no banco mas o fluxo não terminou (as fotos sobem
              antes do checklist) — a contagem de fotos entra nas pendências. */}
          {emAndamento && vistoria && (
            <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
              {vistoria.quantidadeFotos} de {totalFotosEsperado(item.tipoVeiculo)} foto(s)
              {pendencias.length > 0 && <> · falta {pendencias.join(', ')}</>}
            </p>
          )}

          {vistoria && (
            <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
              {([
                rotuloEnvioSga(vistoria.laudoEnviadoSga, 'Laudo'),
                rotuloEnvioSga(vistoria.fotosEnviadasSga, 'Fotos'),
              ] as const).map((chip) => (
                <Chip key={chip.texto} variante={chip.variante}>
                  {chip.texto}
                </Chip>
              ))}
            </div>
          )}

          <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
            {/* Em andamento: continuar ou descartar. "Nova vistoria" fica FORA
                daqui de propósito — iniciar outra criaria uma segunda vistoria
                para o mesmo veículo e deixaria esta órfã para sempre (at-24). */}
            {emAndamento ? (
              <>
                <Botao variante="primario" onClick={() => onContinuarVistoria(item)}>
                  Continuar vistoria
                </Botao>

                {vistoria?.podeDescartar && (
                  <Botao variante="perigo" onClick={() => onDescartar(item)}>
                    Descartar
                  </Botao>
                )}
              </>
            ) : (
              <>
                <Botao
                  variante="primario"
                  disabled={!associado.podeVistoriar}
                  onClick={() => onIniciarVistoria(item)}
                >
                  {vistoria ? 'Nova vistoria' : 'Iniciar vistoria'}
                </Botao>

                {/* Só quando concluída: laudo pela metade sairia sem checklist
                    e sem assinatura. */}
                {vistoria?.concluida && (
                  <Botao
                    variante="secundario"
                    carregando={abrindoLaudo}
                    onClick={() => onVerLaudo(item)}
                  >
                    Ver laudo
                  </Botao>
                )}
              </>
            )}
          </div>

          {/*
            🔴 O motivo em TEXTO, não só no `title`.
            O botão desabilitado tinha a explicação num `title`, que **não existe
            no celular** — e este app é usado de pé, na rua. É a mesma lição que
            fez o laudo listar os bloqueios por extenso (at-24).
          */}
          {!emAndamento && !associado.podeVistoriar && (
            <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-amber-deep">
              Para vistoriar, o associado precisa estar cadastrado no SGA. O envio é
              automático e o sistema tenta a cada 2 horas.
            </p>
          )}
        </section>
      </div>
    </Modal>
  );
};

export default ModalDetalhesVistoria;
